import { Col, Row, Modal, Spin, Input, Select, DatePicker, Table } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DownloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  ClearOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import Button from "../../../../../../components/functional_components/Button/PFButton";
import { ViewMode, Gantt } from "gantt-task-react";
import { get, isEmpty, split, toLower, findKey } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { checkDate, formatNumber, showError, showSuccess, trimData } from "../../../../../../utils/utilities";
import { useParams } from "react-router-dom";
import { dbDate, langDate } from "../../../../../../utils/properties";
import ProjectGanttPlann from "../../../../../../services/api/ProjectGanttPlann";
import { setLoading } from "../../../../../../redux/ducks/Loading";
import html2canvas from "html2canvas";
import "./GanttView.css";
import "gantt-task-react/dist/index.css";
import Legend from "./Legend";
import dayjs from "dayjs";
const { Option } = Select;
const { confirm } = Modal;

const GanttView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const dateFormat = langDate[toLower(currentLanguage)];
  const params = useParams();
  const [state, setState] = useState({
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
    tasks: [],
    dataTable: [],
    columnsTable: [],
    totalTask: "",
    zoom: 1,
    dataTableInit: [],
  });

  const type = [
    {
      value: t("projectsManagement.planning.ganttChart.supplierTask"),
      id: "SUPPLIER_TASK",
    },
    {
      value: t("projectsManagement.planning.ganttChart.internalTask"),
      id: "INTERNAL_TASK",
    },
    {
      value: t("projectsManagement.planning.ganttChart.clientTask"),
      id: "CLIENT_TASK",
    },
    {
      value: t("projectsManagement.planning.ganttChart.milestone"),
      id: "MILESTONE",
    },
    {
      value: t("projectsManagement.planning.ganttChart.kickOff"),
      id: "KICK_OFF",
    },
    {
      value: t("projectsManagement.planning.ganttChart.groupingView"),
      id: "GROUPING_VIEW",
    },
  ];

  useEffect(() => {
    if (isEmpty(state.dataTableInit)) {
      getData(params.id);
    } else {
      buildTable(state.dataTableInit);
    }
  }, [currentLanguage]);

  const getData = async (id) => {
    dispatch(setLoading(true));
    const data = await ProjectGanttPlann.getDataGantt(id);
    if (!data.error) {
      setState((prevState) => ({ ...prevState, totalTask: data.totalItems }));
      buildTasks(data.data);
      getDataTable(params.id);
    } else {
      showError(get(data, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const getDataTable = async (id) => {
    dispatch(setLoading(true));
    const data = await ProjectGanttPlann.getDataGanttTable(id);
    if (!data.error && data.data) {
      setState((prevState) => ({ ...prevState, dataTableInit: data.data }));
      buildTable(data.data);
    } else {
      showError(get(data, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const buildTable = (dataTable) => {
    let columns = [
      {
        title: t("projectsManagement.planning.ganttTable.activity"),
        width: 200,
        dataIndex: "projectTaskName",
        fixed: "left",
      },
    ];
    let values = [];
    dataTable.forEach((dt, i) => {
      const valuePush = {
        key: i,
        projectTaskName: dt.projectTaskName,
        isFather: true,
      };
      let childrenQty = {
        projectTaskName: t("projectsManagement.planning.ganttTable.quantity"),
        key: `childrenQty-${i}`,
      };
      let childrenAmount = {
        projectTaskName: t("projectsManagement.planning.ganttTable.amount"),
        key: `childrenAmount-${i}`,
      };
      Object.keys(dt.amountMap).forEach((am) => {
        if (findKey(columns, (c) => c.dataIndex === am) === undefined) {
          // Write column if not exist yet
          let nameCol = split(am, "-");
          const dateCol = dayjs(nameCol[1], "YYYY").format("YY");
          columns.push({
            title: `${t(`projectsManagement.planning.ganttTable.${toLower(nameCol[0])}`)} / ${dateCol}`,
            width: 100,
            dataIndex: am,
            className: "table-value-right",
          });
        }
        valuePush[am] = "";
        childrenAmount[am] = formatNumber({ value: dt.amountMap[am], lang: currentLanguage, suffix: "EUR" });
      });
      Object.keys(dt.quantityMap).forEach((qm) => {
        childrenQty[qm] = formatNumber({ value: dt.quantityMap[qm], lang: currentLanguage, suffix: "HOUR" });
      });
      values.push(valuePush);
      values.push(childrenQty);
      values.push(childrenAmount);
    });
    setState((prevState) => ({ ...prevState, dataTable: values, columnsTable: columns }));
  };

  const buildTasks = (data) => {
    const arrTask = [];
    data.forEach((item) => {
      let obj = {};
      const startDate = item.startDate.split("-");
      const endDate = item.endDate.split("-");
      let startYear = "";
      let startMonth = "";
      let startDay = "";
      let endYear = "";
      let endMonth = "";
      let endDay = "";
      if (startDate[0].length === 4) {
        startYear = startDate[0];
        startMonth = startDate[1];
        startDay = startDate[2];
        endYear = endDate[0];
        endMonth = endDate[1];
        endDay = endDate[2];
      } else {
        startYear = startDate[2];
        startMonth = startDate[1];
        startDay = startDate[0];
        endYear = endDate[2];
        endMonth = endDate[1];
        endDay = endDate[0];
      }
      switch (item.type) {
        case "SUPPLIER_TASK":
          obj = {
            counter: item.counter,
            start: new Date(startYear, startMonth - 1, startDay),
            end: new Date(endYear, endMonth - 1, endDay),
            name: item.description,
            id: item.id,
            type: "task",
            styles: {
              backgroundColor: "#4086e2",
              backgroundSelectedColor: "#4086e2",
            },
          };
          break;
        case "INTERNAL_TASK":
          obj = {
            counter: item.counter,
            start: new Date(startYear, startMonth - 1, startDay),
            end: new Date(endYear, endMonth - 1, endDay),
            name: item.description,
            id: item.id,
            type: "task",
            styles: {
              backgroundColor: "#81cffc",
              backgroundSelectedColor: "#81cffc",
            },
          };
          break;
        case "CLIENT_TASK":
          obj = {
            counter: item.counter,
            start: new Date(startYear, startMonth - 1, startDay),
            end: new Date(endYear, endMonth - 1, endDay),
            name: item.description,
            id: item.id,
            type: "task",
          };
          break;
        case "MILESTONE":
          obj = {
            counter: item.counter,
            start: new Date(startYear, startMonth - 1, startDay),
            end: new Date(endYear, endMonth - 1, endDay),
            name: item.description,
            id: item.id,
            type: "milestone",
            styles: {
              backgroundColor: "red",
              backgroundSelectedColor: "red",
            },
          };
          break;
        case "KICK_OFF":
          obj = {
            counter: item.counter,
            start: new Date(startYear, startMonth - 1, startDay),
            end: new Date(endYear, endMonth - 1, endDay),
            name: item.description,
            id: item.id,
            type: "milestone",
            styles: {
              backgroundColor: "blue",
              backgroundSelectedColor: "blue",
            },
          };
          break;
        case "GROUPING_VIEW":
          obj = {
            counter: item.counter,
            start: new Date(startYear, startMonth - 1, startDay),
            end: new Date(endYear, endMonth - 1, endDay),
            name: item.description,
            id: item.id,
            progress: 100,
            type: "project",
            styles: {
              backgroundColor: "#5e97e2",
              backgroundSelectedColor: "#5e97e2",
              progressColor: "#fff",
              progressSelectedColor: "#fff",
            },
          };
          break;
        default:
          break;
      }
      arrTask.push(obj);
    });
    setState((prevState) => ({ ...prevState, tasks: arrTask }));
  };

  const handleTaskChange = async (task) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    if (task.start) {
      task.start = dayjs(task.start, dateFormat).format(dbDate);
    } else {
      task.start = null;
    }
    if (task.end) {
      task.end = dayjs(task.end, dateFormat).format(dbDate);
    } else {
      task.end = null;
    }
    let data = {
      description: task.name,
      type: getType(task.type, task.styles),
      startDate: task.start,
      endDate: task.end,
      id: task.id,
    };
    const response = await ProjectGanttPlann.addTask(params.id, data);
    if (!response.error) {
      getData(params.id);
      closeModal();
    } else {
      showError(get(response, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const handleDblClick = (task) =>
    setState((prevState) => ({ ...prevState, modal: { visible: true, type: "edit", data: task } }));

  const createTaskListLocal = (obj) => {
    return (
      <div className="Gantt-Task-List_Wrapper" style={{ fontFamily: obj.fontFamily, fontSize: obj.fontSize }}>
        {state.tasks.map((t) => {
          return (
            <div
              className="Gantt-Task-List_Row"
              style={{ height: obj.rowHeight, display: "flex", justifyContent: "space-between", alignItems: "center" }}
              key={`${t.id}row`}
              onClick={() => {
                if (obj.selectedTaskId === t.id) {
                  obj.setSelectedTask("");
                } else {
                  obj.setSelectedTask(t.id);
                }
              }}
            >
              <div className="Gantt-Task-List_Cell">
                <div
                  className={
                    obj.selectedTaskId === t.id ? "Gantt-Task-List-Checkbox__Checked" : "Gantt-Task-List-Checkbox"
                  }
                ></div>
              </div>
              <div className="Gantt-Task-List_Cell" style={{ width: "180px" }} title={t.name}>
                <div
                  className="Gantt-Task-List_Name-Container gantt-container-flex "
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <div className="Gantt-Task-List_Cell__Link" onClick={() => obj.onClick(t)}>
                    {t.name}
                  </div>
                  <div className="space-between">
                    <div className="mr-10">
                      <div
                        onClick={
                          userInfo.canEditPlanning && projectManagement.enabled && t.counter !== 1
                            ? changePosition(t.id, false)
                            : null
                        }
                      >
                        <CaretUpOutlined
                          className={
                            userInfo.canEditPlanning && t.counter !== 1 && projectManagement.enabled
                              ? "cursor-pointer"
                              : "disabled"
                          }
                        />
                      </div>
                      <div
                        onClick={
                          userInfo.canEditPlanning && projectManagement.enabled && t.counter !== state.totalTask
                            ? changePosition(t.id, true)
                            : null
                        }
                      >
                        <CaretDownOutlined
                          className={
                            userInfo.canEditPlanning && t.counter !== state.totalTask && projectManagement.enabled
                              ? "cursor-pointer"
                              : "disabled"
                          }
                        />
                      </div>
                    </div>
                    <div
                      onClick={userInfo.canEditPlanning && projectManagement.enabled ? deleteCallback(t.id) : null}
                      className="mr-10"
                    >
                      <DeleteOutlined
                        className={
                          userInfo.canEditPlanning && projectManagement.enabled
                            ? "icon-small"
                            : "icon-small noCursorPointer disabled"
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const createHeaderLocal = () => {
    return (
      <div className="_3_ygE">
        <div className="_1nBOt" style={{ height: "48px" }}>
          <div className="_WuQ0f" style={{ minWidth: "200px", paddingLeft: "15px" }}>
            &nbsp;Name
          </div>
        </div>
      </div>
    );
  };

  const changePosition = (id, boolean) => async () => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const response = await ProjectGanttPlann.changePosition(params.id, id, `increment=${boolean}`);
    if (!response.error) {
      getData(params.id);
    } else {
      showError(get(response, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = (id) => () => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const deleteResponse = await ProjectGanttPlann.deletetask(params.id, id);
        if (!deleteResponse.error) {
          showSuccess();
          getData(params.id);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const closeModal = () => {
    formikGanttForm.resetForm();
    setState((prevState) => ({
      ...prevState,
      modal: { visible: false, type: "", data: {} },
    }));
  };

  const getType = (type, style) => {
    let whatTaskIs = "";
    switch (type) {
      case "project":
        whatTaskIs = "GROUPING_VIEW";
        break;
      case "task":
        switch (style.backgroundColor) {
          case "#4086e2":
            whatTaskIs = "SUPPLIER_TASK";
            break;
          case "#81cffc":
            whatTaskIs = "INTERNAL_TASK";
            break;
          case "#b8c2cc":
            whatTaskIs = "CLIENT_TASK";
            break;
          default:
            break;
        }
        break;
      case "milestone":
        switch (style.backgroundColor) {
          case "red":
            whatTaskIs = "MILESTONE";
            break;
          case "blue":
            whatTaskIs = "KICK_OFF";
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }
    return whatTaskIs;
  };

  const formikGanttForm = useFormik({
    initialValues: {
      description: get(state.modal, "data.name", ""),
      type: getType(get(state.modal, "data.type", ""), get(state.modal, "data.styles", {})),
      startDate:
        get(state.modal, "data.start", null) !== null ? dayjs(state.modal.data.start, dbDate).format(dateFormat) : null,
      endDate:
        get(state.modal, "data.end", null) !== null ? dayjs(state.modal.data.end, dbDate).format(dateFormat) : null,
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.description) errors.description = "Required!";
      if (!values.type) errors.type = "Required!";
      if (values.startDate && values.endDate && checkDate(values.startDate, values.endDate, dateFormat)) {
        errors.startDate = "Required!";
        errors.endDate = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      if (values.startDate) {
        values.startDate = dayjs(values.startDate, dateFormat).format(dbDate);
      } else {
        values.startDate = null;
      }
      if (values.endDate) {
        values.endDate = dayjs(values.endDate, dateFormat).format(dbDate);
      } else {
        values.endDate = null;
      }
      let data = {
        description: values.description,
        type: values.type,
        startDate: values.startDate,
        endDate: values.endDate,
        id: get(state.modal, "data.id", undefined),
      };
      const response = await ProjectGanttPlann.addTask(params.id, data);
      if (!response.error) {
        showSuccess();
        getData(params.id);
        closeModal();
      } else {
        showError(get(response, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const downloadGantt = () => {
    confirm({
      title: t("projectsManagement.planning.ganttChart.download"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const input = document.getElementById("Gantt");
        return html2canvas(input).then((canvas) => {
          const imgData = canvas.toDataURL("img/png");
          var link = document.createElement("a");
          link.download = "Gantt.png";
          link.href = imgData;
          link.click();
        });
      },
      onCancel() {},
    });
  };

  const openModal = (type) => () => {
    return setState((prevState) => ({ ...prevState, modal: { visible: true, type: type, data: {} } }));
  };
  const changeZoom = (type) => () => {
    if (type === "+") {
      setState((prevState) => ({ ...prevState, zoom: state.zoom + 0.1 }));
    } else if (type === "-") {
      setState((prevState) => ({ ...prevState, zoom: state.zoom - 0.1 }));
    } else {
      setState((prevState) => ({ ...prevState, zoom: 1 }));
    }
  };

  return (
    <>
      <Row type="flex" align="middle" justify="space-between" className="mb-10">
        <Col xl={8} lg={16} md={24} xs={24} sm={24} className="mb-10">
          <Legend />
        </Col>
        <Col xl={12} lg={8} md={24} xs={24} sm={24} className="mb-10">
          <Row type="flex" justify="end" className="mb-10">
            <Button
              isDisabled={!userInfo.canEditPlanning || !projectManagement.enabled}
              className="mr-10"
              currentIcon={<PlusOutlined className="icon-medium" />}
              clickCallback={openModal("new")}
            />
            <Button currentIcon={<DownloadOutlined className="icon-medium" />} clickCallback={downloadGantt} />
          </Row>
          <Row type="flex" justify="end" className="mb-10">
            <Button
              type="secondary"
              isDisabled={state.zoom === 1}
              className="mr-10"
              currentIcon={<ClearOutlined className="icon-medium" />}
              clickCallback={changeZoom()}
            />
            <Button
              isDisabled={state.zoom > 1}
              className="mr-10"
              currentIcon={<ZoomInOutlined className="icon-medium" />}
              clickCallback={changeZoom("+")}
            />
            <Button
              isDisabled={state.zoom < 0.2}
              currentIcon={<ZoomOutOutlined className="icon-medium" />}
              clickCallback={changeZoom("-")}
            />
          </Row>
        </Col>
      </Row>
      {!isEmpty(state.tasks) && state.tasks.length == state.totalTask && (
        <div id="Gantt" style={{ zoom: state.zoom }}>
          <Gantt
            locale={toLower(currentLanguage === "GB" ? "EN" : currentLanguage)} // funziona con il multilingua ma non lo rirenderizza nella pagina
            tasks={state.tasks}
            viewMode={ViewMode.Week}
            onDateChange={userInfo.canEditPlanning ? handleTaskChange : null}
            onDoubleClick={userInfo.canEditPlanning ? handleDblClick : null}
            listCellWidth={100}
            columnWidth={80}
            rowHeight={50}
            fontSize={12}
            TaskListTable={createTaskListLocal}
            TaskListHeader={createHeaderLocal}
            handleWidth={4}
            todayColor={"#0026544d"}
          />
        </div>
      )}

      <Table
        bordered
        className="mt-10"
        size="small"
        columns={state.columnsTable}
        dataSource={state.dataTable}
        scroll={{ x: 200 }}
        pagination={false}
        rowClassName={(record) => (record.isFather ? "table-secondary" : "")}
      />

      {state.modal.visible && (
        <Modal
          width={700}
          title={
            state.modal.type === "edit" ? (
              <b>{t("projectsManagement.ProjectForm.contractualRevenues.modal.edit")}</b>
            ) : (
              <b>{t("projectsManagement.ProjectForm.contractualRevenues.modal.new")}</b>
            )
          }
          open={state.modal.visible}
          onCancel={closeModal}
          footer={[
            <Button
              key="okButton"
              currentIcon={<SaveOutlined className="icon-small" />}
              clickCallback={formikGanttForm.submitForm}
              isDisabled={!(formikGanttForm.dirty && formikGanttForm.isValid)}
            />,
          ]}
        >
          {state.loading && (
            <div className="container-spinner-positioning">
              <Spin spinning={state.loading} className="spinner-positioning" />
            </div>
          )}
          <Row gutter={[40, 48]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <Col span={12}>
                  <label className="fw-bold " htmlFor="description">
                    {t("projectsManagement.planning.ganttChart.modal.description")}*
                  </label>
                </Col>
              </Row>
              <Row>
                <Input
                  required
                  id="description"
                  name="description"
                  onChange={formikGanttForm.handleChange}
                  onBlur={formikGanttForm.handleBlur}
                  value={formikGanttForm.values.description}
                />
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold" htmlFor="type">
                  {t("projectsManagement.planning.ganttChart.modal.type")}*
                </label>
              </Row>
              <Row>
                <Select
                  showSearch
                  placeholder={t("projectsManagement.planning.ganttChart.modal.type")}
                  id="type"
                  name="type"
                  className="w-100"
                  onBlur={formikGanttForm.handleBlur("type")}
                  onChange={formikGanttForm.handleChange("type")}
                  value={formikGanttForm.values.type}
                >
                  {type.map((item) => (
                    <Option key={item.id} value={item.id} name={item.value}>
                      {item.value}
                    </Option>
                  ))}
                </Select>
              </Row>
            </Col>
          </Row>
          <Row gutter={[40, 48]} className={"mt-10"}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <Col span={12}>
                  <label className="fw-bold " htmlFor="startDate">
                    {t("projectsManagement.planning.ganttChart.modal.startDate")}*
                  </label>
                </Col>
              </Row>
              <Row>
                <DatePicker
                  picker="day"
                  id="startDate"
                  name="startDate"
                  format={dateFormat}
                  locale={langDate[`locale_${toLower(currentLanguage)}`]}
                  value={
                    formikGanttForm.values.startDate ? dayjs(formikGanttForm.values.startDate, dateFormat) : undefined
                  }
                  onChange={(_, dateString) => {
                    formikGanttForm.setFieldValue("startDate", dateString);
                  }}
                />
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold" htmlFor="endDate">
                  {t("projectsManagement.planning.ganttChart.modal.endDate")}*
                </label>
              </Row>
              <Row>
                <DatePicker
                  picker="day"
                  id="endDate"
                  name="endDate"
                  format={dateFormat}
                  locale={langDate[`locale_${toLower(currentLanguage)}`]}
                  value={formikGanttForm.values.endDate ? dayjs(formikGanttForm.values.endDate, dateFormat) : undefined}
                  onChange={(_, dateString) => {
                    formikGanttForm.setFieldValue("endDate", dateString);
                  }}
                />
              </Row>
            </Col>
          </Row>
        </Modal>
      )}
    </>
  );
};

export default GanttView;
